import React from "react";
import { Search, X, MapPin, Sparkles, Filter, ChevronDown, Calendar, DollarSign, Clock, Utensils, Tag, Star, ArrowUpDown } from "lucide-react";

const formatLabel = (value) => {
  if (value == null) return value;
  const raw = String(value).trim();
  if (!raw) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

function FilterPanel({ filters, setFilters, options, lockedFields = [], hideBudget = false }) {
  const isLocked = (field) => lockedFields.includes(field);
  return (
    <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-gray-100 overflow-hidden sticky top-8">
      <div className="p-4 border-b border-gray-50 bg-gradient-to-r from-blue-50/50 to-transparent">
        <div className="flex items-center gap-2 mb-1">
          <Filter size={18} className="text-blue-600" />
          <h2 className="font-bold text-gray-900 text-sm">Refine Search</h2>
        </div>
        <p className="text-xs text-gray-500 font-medium">Customize your yoga journey</p>
      </div>

      <div className="p-4 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
        {/* Name Search */}
        <div className="space-y-2">
          <label htmlFor="filter-keywords" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
            <Search size={12} /> Keywords
          </label>
          <div className="relative">
            <input
              id="filter-keywords"
              name="keywords"
              type="text"
              placeholder="Search center name..."
              value={filters.nameQuery}
              onChange={(e) => setFilters({ ...filters, nameQuery: e.target.value })}
              className="w-full pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-gray-400"
            />
          </div>
        </div>

        {/* Location Dropdown */}
        {!isLocked("location") && (
          <div className="space-y-2">
            <label htmlFor="filter-location" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin size={12} /> Location
            </label>
            <div className="relative">
              <select
                id="filter-location"
                name="location"
                value={filters.locationQuery}
                onChange={(e) => setFilters({ ...filters, locationQuery: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">All Destinations</option>
                {(options?.locations || []).map((l) => (
                  <option key={l} value={l}>{formatLabel(l)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* Categories Grid */}
        <div className="grid grid-cols-1 gap-3">
          {!isLocked("type") && (
            <div className="space-y-2">
              <label htmlFor="filter-course-type" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <Sparkles size={12} /> Course Type
              </label>
              <div className="relative">
                <select
                  id="filter-course-type"
                  name="courseType"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
                >
                  <option value="">Any Type</option>
                  {(options?.types || []).map((t) => (
                    <option key={t} value={t}>{formatLabel(t)}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="filter-yoga-style" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Sparkles size={12} /> Yoga Style
            </label>
            <div className="relative">
              <select
                id="filter-yoga-style"
                name="yogaStyle"
                value={filters.style}
                onChange={(e) => setFilters({ ...filters, style: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">Any Style</option>
                {(options?.styles || []).map((s) => (
                  <option key={s} value={s}>{formatLabel(s)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Utensils size={12} /> Meals
            </label>
            <div className="relative">
              <select
                value={filters.meals}
                onChange={(e) => setFilters({ ...filters, meals: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">Any Meals</option>
                {(options?.meals || []).map((m) => (
                  <option key={m} value={m}>{formatLabel(m)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Tag size={12} /> Source
            </label>
            <div className="relative">
              <select
                value={filters.source}
                onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">Any Source</option>
                {(options?.sources || []).map((src) => (
                  <option key={src} value={src}>{formatLabel(src)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Price & Duration */}
        <div className="space-y-3 pt-1">
          {!hideBudget && (
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <DollarSign size={12} /> Max Budget
              </label>
              <input
                type="number"
                placeholder="Amount in USD"
                value={filters.maxPrice}
                onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                className="w-full pl-4 pr-4 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-gray-400"
              />
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              <Clock size={12} /> Duration
            </label>
            <div className="relative">
              <select
                value={filters.duration}
                onChange={(e) => setFilters({ ...filters, duration: e.target.value })}
                className="w-full appearance-none pl-4 pr-10 py-2 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 transition-all"
              >
                <option value="">Any Duration</option>
                {(options?.durations || []).map((d) => (
                  <option key={d} value={d}>{formatLabel(d)}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              From
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 bg-gray-50 border-none rounded-xl text-[12px] focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
              To
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 bg-gray-50 border-none rounded-xl text-[12px] focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>
        </div>

        <button
          onClick={() =>
            setFilters({
              nameQuery: "",
              locationQuery: "",
              type: "",
              style: "",
              meals: "",
              source: "",
              duration: "",
              maxPrice: "",
              sort: "",
              startDate: "",
              endDate: "",
            })
          }
          className="w-full mt-3 py-2.5 bg-gray-900 text-white rounded-2xl text-sm font-bold hover:bg-gray-800 active:scale-95 transition-all shadow-lg shadow-gray-200"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
}

export default FilterPanel;
